import { useCallback, useEffect, useState } from "react";

import { Button, Form, Modal, FormLabel } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { api } from "../../utils/api";

import { getAssignUserCustomErrorMessage } from "../../utils/errorMessages";
import type z from "zod";
import { schemas } from "../../api/generated";

type FileDTO = z.infer<typeof schemas.FileWithUsage>;
const ProblemDetails = schemas.ProblemDetails;

const getAllUsers = async (token: string) => {
  // HACK: We don't expect more than 1000 users in the system for now but once we get to that point
  // we'll need to implement virtualization or pagination here.
  return api
    .getUsers(token, 1000, 0, "createdAt_desc")
    .then((data) => data.data);
};

const warning =
  "This action will overwrite any existing Attack Library assignments for the selected user. Users shown with a link (🔗) icon already have a file assigned.";
const libraryCipherNote =
  "Due to current design limitations, the selected user's password and the encryption cipher of the Attack Library file must match for devices to be able download and decrypt them.";

type UserSelection = {
  id: string;
  name: string;
  // files: { id: string; fileName: string }[];
  files: string[];
};

export function FileAssignmentModal({
  showAssignmentModal,
  setShowAssignmentModal,
  selectedFile,
  token,
  onFileAssigned,
}: {
  showAssignmentModal: boolean;
  // eslint-disable-next-line no-unused-vars
  setShowAssignmentModal: (show: boolean) => void;
  selectedFile?: FileDTO | undefined;
  token: string;
  // eslint-disable-next-line no-unused-vars
  onFileAssigned?: (file: FileDTO) => void;
}) {
  const [users, setUsers] = useState<UserSelection[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSelection | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAllUsers(token)
      .then((users) => {
        setUsers(
          users
            .sort(
              (a, b) =>
                a.firstname.localeCompare(b.firstname, undefined, {
                  sensitivity: "case",
                }) ||
                a.lastname.localeCompare(b.lastname, undefined, {
                  sensitivity: "case",
                })
            )
            .map((user) => ({
              id: user._id!,
              name: `${user.firstname} ${user.lastname}`,
              files: user.files,
            }))
        );
      })
      .catch(() => {
        toast.error(
          "Failed to load users. Please refresh the page and try again later."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token]);

  const handleAssignFile = useCallback(async () => {
    const userId = selectedUser?.id;
    const fileId = selectedFile?.id;
    if (!userId || !fileId) {
      console.warn("User or file ID is missing", { userId, fileId });
      return toast.error("User and file are required");
    }

    if (selectedUser.files.length > 0) {
      // Unassign existing files from user as an attempt to clean up assignments
      // for as long as the backend still supports multiple file assignments.
      const assignedFiles = await api
        .getUserFiles(token, userId)
        .then((res) => res.files);
      for (const file of assignedFiles) {
        try {
          await api.unassignFileFromUser(token, file.id!, userId);
        } catch {
          toast.error(
            `Failed to unassign file ${file.fileName} from user ${selectedUser.name}.`
          );
        }
      }
    }

    try {
      const res = await api.assignFileToUser(token, {
        userId,
        fileId,
      });

      const toastId = toast.success(res.message); // FUTURE: Consider using a frontend driven message here
      onFileAssigned?.(selectedFile);
      return toastId;
    } catch (err) {
      return toast.error(
        await ProblemDetails.parseAsync(err)
          .then(getAssignUserCustomErrorMessage)
          .catch(() => "Unknown error")
      );
    } finally {
      setShowAssignmentModal(false);
      setSelectedUser(undefined);
    }
  }, [
    token,
    selectedFile,
    selectedUser,
    onFileAssigned,
    setShowAssignmentModal,
  ]);

  return (
    <Modal
      show={showAssignmentModal}
      onHide={() => setShowAssignmentModal(false)}
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>Assign File</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <Form.Group>
          <FormLabel htmlFor="user-input">
            Choose a user to assign this file to:
          </FormLabel>
          <Form.Select
            id={`user-input`}
            autoFocus
            value={selectedUser?.id || ""}
            onChange={(e) =>
              setSelectedUser(users.find((user) => user.id === e.target.value))
            }
          >
            <option value="">Assign to a user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
                {user.files.length > 0 && " 🔗"}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        {selectedUser && selectedUser?.files?.length > 0 && (
          <p className="text-body-secondary my-0">
            <small>
              <em>
                <FontAwesomeIcon icon={faWarning} className="text-warning" />{" "}
                {warning}
              </em>
            </small>
          </p>
        )}
        <Form.Group>
          <FormLabel className="me-1">File:</FormLabel>
          <code>
            <mark>{selectedFile?.fileName}</mark>
          </code>
        </Form.Group>
        <p className="text-body-secondary my-0">
          <small>
            <em>
              <FontAwesomeIcon icon={faCircleInfo} className="text-info" />{" "}
              {libraryCipherNote}
            </em>
          </small>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          disabled={isLoading}
          onClick={() => setShowAssignmentModal(false)}
        >
          Cancel
        </Button>
        <Button
          variant="dark"
          disabled={isLoading || !selectedUser}
          onClick={handleAssignFile}
        >
          Assign
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
